import $ from 'jquery';
import 'devextreme/ui/html_editor';
import 'devextreme/ui/html_editor/converters/markdown';

// Default editor instance for backward compatibility
let defaultEditor = null;

class EnciHtmlEditor {
  constructor(elementId, options = {}) {
    this.elementId = elementId;
    this.defaultToolbarItems = [
      'undo',
      'redo',
      'separator',
      {
        name: 'size',
        acceptedValues: ['8pt', '10pt', '12pt', '14pt', '18pt', '24pt', '36pt'],
        options: { inputAttr: { 'aria-label': 'Font size' } },
      },
      {
        name: 'font',
        acceptedValues: [
          'Arial',
          'Courier New',
          'Georgia',
          'Impact',
          'Lucida Console',
          'Tahoma',
          'Times New Roman',
          'Verdana',
        ],
        options: { inputAttr: { 'aria-label': 'Font family' } },
      },
      'separator',
      'bold',
      'italic',
      'strike',
      'underline',
      'separator',
      'alignLeft',
      'alignCenter',
      'alignRight',
      'alignJustify',
      'separator',
      'orderedList',
      'bulletList',
      'separator',
      {
        name: 'header',
        acceptedValues: [false, 1, 2, 3, 4, 5],
        options: { inputAttr: { 'aria-label': 'Header' } },
      },
      'separator',
      'color',
      'background',
      'separator',
      'link',
      'image',
      'separator',
      'clear',
      'codeBlock',
      'blockquote',
      'separator',
      'insertTable',
      'deleteTable',
      'insertRowAbove',
      'insertRowBelow',
      'deleteRow',
      'insertColumnLeft',
      'insertColumnRight',
      'deleteColumn',
    ];

    this.options = {
      height: options.height || 300,
      valueType: 'html',
      tableContextMenu: { enabled: true },
      tableResizing: { enabled: true },
      imageUpload: {
        tabs: ['file', 'url'],
        fileUploadMode: 'base64',
      },
      toolbar: {
        items: options.toolbarItems || this.defaultToolbarItems,
      },
      mediaResizing: {
        enabled: true,
      },
      ...options,
    };

    this.editor = null;
  }

  /**
   * Initialize the HTML editor
   * @returns {Object} The editor instance
   */
  initialize() {
    if (!this.elementId) {
      throw new Error('Element ID is required to initialize the editor');
    }

    const element = $(`#${this.elementId}`);
    if (!element.length) {
      throw new Error(`Element with ID '${this.elementId}' not found`);
    }

    this.editor = element
      .dxHtmlEditor({
        ...this.options,
        onValueChanged: e => {
          if (this.contentChangeCallback) {
            this.contentChangeCallback(e.value);
          }
        },
      })
      .dxHtmlEditor('instance');

    return this.editor;
  }

  /**
   * Set up a callback for content changes
   * @param {Function} callback - Function to call when content changes
   */
  onContentChange(callback) {
    this.contentChangeCallback = callback;
  }

  /**
   * Get the current value of the editor
   * @returns {string} The editor content
   */
  getValue() {
    if (!this.editor) {
      throw new Error('Editor not initialized. Call initialize() first.');
    }
    return this.editor.option('value');
  }

  /**
   * Set the value of the editor
   * @param {string} content - The content to set
   */
  setValue(content) {
    if (!this.editor) {
      throw new Error('Editor not initialized. Call initialize() first.');
    }
    this.editor.option('value', content);
  }

  /**
   * Clear the editor content
   */
  clear() {
    this.setValue('');
  }

  /**
   * Check if the editor exists and is initialized
   * @returns {boolean}
   */
  exists() {
    return !!this.editor;
  }

  /**
   * Dispose of the editor instance
   */
  dispose() {
    if (this.editor) {
      this.editor.dispose();
      this.editor = null;
    }
  }

  /**
   * Static method to create and initialize an editor instance
   * @param {string} elementId - The ID of the element to convert to an editor
   * @param {Object} options - Editor options
   * @returns {EnciHtmlEditor} The initialized editor instance
   */
  static create(elementId, options = {}) {
    const editor = new EnciHtmlEditor(elementId, options);
    editor.initialize();
    return editor;
  }
}

// Export the interface methods for the enci namespace
export const clearEditor = () => {
  if (!defaultEditor) {
    defaultEditor = EnciHtmlEditor.create('form-emailer-message');
  }
  defaultEditor.clear();
};

export const setEditorValue = content => {
  if (!defaultEditor) {
    defaultEditor = EnciHtmlEditor.create('form-emailer-message');
  }
  defaultEditor.setValue(content);
};

export const getHtmlEditorValue = () => {
  if (!defaultEditor) {
    defaultEditor = EnciHtmlEditor.create('form-emailer-message');
  }
  return defaultEditor.getValue();
};

export const initialize = () => {
  if (!defaultEditor) {
    defaultEditor = EnciHtmlEditor.create('form-emailer-message');
  }
};

// Export the class for direct usage
export { EnciHtmlEditor };
